import { HttpError } from '@revelio/data-access';
import type { FilterSet, FilterSetScope } from '@revelio/iso-utility';

import {
  DashMetaHttpError,
  dashMetaJson,
} from '../../../shared/api/dash-meta-fetch';

export class FilterSetRequestError extends HttpError {
  constructor(
    message: string,
    status: number,
    public readonly conflictingId?: string,
    public readonly requestId?: string
  ) {
    super(message, status);
    this.name = 'FilterSetRequestError';
  }
}

// Network, abort and JSON parse failures reach here too, as plain errors with
// no status.
const toRequestError = (error: unknown): FilterSetRequestError => {
  const httpError = error instanceof DashMetaHttpError ? error : undefined;
  const errorMessage = error instanceof Error ? error.message : '';
  let parsed: { message?: unknown; conflictingId?: unknown } | undefined;
  try {
    parsed = JSON.parse(errorMessage) as typeof parsed;
  } catch {
    parsed = undefined;
  }

  const { message } = parsed ?? {};
  const readable = Array.isArray(message) ? message.join('; ') : message;

  return new FilterSetRequestError(
    typeof readable === 'string' && readable.length > 0
      ? readable
      : errorMessage || 'Unknown error',
    httpError?.status ?? 0,
    typeof parsed?.conflictingId === 'string'
      ? parsed.conflictingId
      : undefined,
    httpError?.requestId
  );
};

async function requestFilterSets<T>(
  path: string,
  init?: RequestInit
): Promise<T | undefined> {
  try {
    const body = await dashMetaJson<{ data: T } | undefined>(
      `/api/filter-sets${path}`,
      init
    );
    return body?.data;
  } catch (error) {
    throw toRequestError(error);
  }
}

export const getFilterSets = async (userId?: string): Promise<FilterSet[]> =>
  (await requestFilterSets<FilterSet[]>(
    userId ? `?userId=${encodeURIComponent(userId)}` : ''
  )) ?? [];

export const createFilterSet = (input: {
  name: string;
  scope: FilterSetScope;
  url: string;
}) =>
  requestFilterSets<FilterSet>('', {
    method: 'POST',
    body: JSON.stringify(input),
  });

export const updateFilterSet = (
  id: string,
  input: { name?: string; scope?: FilterSetScope; url?: string }
) =>
  requestFilterSets<FilterSet>(`/${id}`, {
    method: 'POST',
    body: JSON.stringify(input),
  });

export const deleteFilterSet = (id: string) =>
  requestFilterSets<undefined>(`/${id}`, { method: 'DELETE' });
